import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Radio,
  RadioGroup,
  Switch,
  Text,
  VStack,
  useBoolean,
} from '@chakra-ui/react';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { QUIZZES_EDITED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { RootState, store, useAppSelector } from 'modules/Editor/store';
import { selectWidget, selectWidgets } from 'modules/Editor/store/infographSelector';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetId } from 'types/idTypes';
import { removeAtIndex } from 'utils/array';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { getTextWidgetProperties } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetData.helpers';
import { useWidget } from 'widgets/sdk';

import { ANSWERS_LIMIT } from '../../MultipleChoicesWidget.config';
import {
  ComponentWidgetIdKeys,
  MultipleChoicesType,
  MultipleChoicesWidgetData,
} from '../../MultipleChoicesWidget.types';
import { mergeMultipleChoiceWidgetData, updateCorrectAnswers } from '../MultipleChoicesWidgetSideMenuSettings.helpers';

/**
 * Render the question text on the side panel settings
 */
const QuestionText = () => {
  const { componentWidgetIdMap } = useWidget<MultipleChoicesWidgetData>();

  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const questionText =
    getTextWidgetProperties((useAppSelector(selectWidget(questionWidgetId)) as TextWidgetData)?.proseMirrorData)
      ?.text || '';

  return (
    <Box w='full'>
      <Text lineHeight={6} fontSize={'sm'} color={'font.500'}>
        {questionText}
      </Text>
    </Box>
  );
};

/**
 * Render the correct answers config on the side panel settings
 */
const CheckBoxCorrectSection = () => {
  const { t } = useTranslation('editor_widget_toolbar', {
    keyPrefix: 'multipleChoicesToolbar',
    useSuspense: false,
  });
  const widgets = useAppSelector(selectWidgets);
  const { widgetId, componentWidgetIdMap, correctAnswers, replaceWidget, updateWidget, memberWidgetIds } =
    useWidget<MultipleChoicesWidgetData>();

  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const answerWidgetIdList = memberWidgetIds.filter((id) => id !== questionWidgetId);
  const answerWidgetTextList = answerWidgetIdList
    .map((id) => widgets?.[id])
    .map((widget) => getTextWidgetProperties((widget as TextWidgetData)?.proseMirrorData)?.text || '');
  const memberWidgetList = memberWidgetIds.map(
    (id) => (store.getState() as RootState).infograph.widgets[id] as TextWidgetData,
  );
  const disableAddButton = answerWidgetIdList.length >= ANSWERS_LIMIT;

  const handleCheckboxChange = (value: string[]) => {
    updateWidget({
      correctAnswers: value,
    });
  };

  const handleDeleteChoice = useCallback(
    (targetWidgetId: WidgetId) => {
      let currentWidgetData = (store.getState() as RootState).infograph.widgets[widgetId] as MultipleChoicesWidgetData;
      currentWidgetData = {
        ...currentWidgetData,
        correctAnswers: updateCorrectAnswers(correctAnswers, answerWidgetIdList.indexOf(targetWidgetId)),
      }; // Update correct answers order

      const memberWidgetIndex = currentWidgetData.memberWidgetIds.findIndex((id) => id === targetWidgetId);

      replaceWidget(
        mergeMultipleChoiceWidgetData(
          'delete',
          MultipleChoicesType.Horizontal,
          currentWidgetData,
          removeAtIndex(currentWidgetData.memberWidgetIds, memberWidgetIndex),
          removeAtIndex(memberWidgetList, memberWidgetIndex),
        ),
      );
    },
    [widgetId, memberWidgetList, answerWidgetIdList, correctAnswers, replaceWidget],
  );

  const handleAddChoice = useCallback(() => {
    const currentWidgetData = (store.getState() as RootState).infograph.widgets[widgetId] as MultipleChoicesWidgetData;
    replaceWidget(
      mergeMultipleChoiceWidgetData(
        'add',
        MultipleChoicesType.Horizontal,
        currentWidgetData,
        memberWidgetIds,
        memberWidgetList,
      ),
    );
  }, [widgetId, memberWidgetList, memberWidgetIds, replaceWidget]);

  return (
    <Box w='full'>
      <CheckboxGroup colorScheme='green' defaultValue={[]} value={correctAnswers} onChange={handleCheckboxChange}>
        <Flex gap='2' flexDir={'column'}>
          {answerWidgetIdList.map((widgetId, index) => (
            <Flex key={widgetId} justifyContent={'flex-start'} w={'full'} p={0} m={0}>
              <Checkbox value={index.toString()} w='full'>
                <Text lineHeight={'6'} fontSize={'sm'} color={'font.500'} w={'full'}>
                  {answerWidgetTextList[index]}
                </Text>
              </Checkbox>
              <Button
                variant='unstyled'
                minW='20px'
                width='20px'
                height='20px'
                onClick={() => handleDeleteChoice(widgetId)}
              >
                <DeleteIcon width='20px' height='20px' color='var(--vg-colors-font-500)' />
              </Button>
            </Flex>
          ))}
        </Flex>
      </CheckboxGroup>
      <Button variant='action' isDisabled={disableAddButton} borderRadius={1} w='full' mt={4} onClick={handleAddChoice}>
        {t('settingsSideBar.multipleChoices.addButton')}
      </Button>
    </Box>
  );
};

/**
 * Render the correct answers config on the side panel settings
 */
const RadioCorrectSection = () => {
  const { t } = useTranslation('editor_widget_toolbar', {
    keyPrefix: 'multipleChoicesToolbar',
    useSuspense: false,
  });
  const widgets = useAppSelector(selectWidgets);
  const { widgetId, componentWidgetIdMap, correctAnswers, replaceWidget, updateWidget, memberWidgetIds } =
    useWidget<MultipleChoicesWidgetData>();

  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const answerWidgetIdList = memberWidgetIds.filter((id) => id !== questionWidgetId);
  const answerWidgetTextList = answerWidgetIdList
    .map((id) => widgets?.[id])
    .map((widget) => getTextWidgetProperties((widget as TextWidgetData)?.proseMirrorData)?.text || '');
  const memberWidgetList = memberWidgetIds.map(
    (id) => (store.getState() as RootState).infograph.widgets[id] as TextWidgetData,
  );
  const disableAddButton = answerWidgetIdList.length >= ANSWERS_LIMIT;

  const handleRadioChange = (value: string) => {
    updateWidget({
      correctAnswers: [value],
    });
  };

  const handleDeleteChoice = useCallback(
    (targetWidgetId: WidgetId) => {
      let currentWidgetData = (store.getState() as RootState).infograph.widgets[widgetId] as MultipleChoicesWidgetData;
      currentWidgetData = {
        ...currentWidgetData,
        correctAnswers: updateCorrectAnswers(correctAnswers, answerWidgetIdList.indexOf(targetWidgetId)),
      }; // Update correct answers order

      const memberWidgetIndex = currentWidgetData.memberWidgetIds.findIndex((id) => id === targetWidgetId);

      replaceWidget(
        mergeMultipleChoiceWidgetData(
          'delete',
          MultipleChoicesType.Horizontal,
          currentWidgetData,
          removeAtIndex(currentWidgetData.memberWidgetIds, memberWidgetIndex),
          removeAtIndex(memberWidgetList, memberWidgetIndex),
        ),
      );
    },
    [widgetId, memberWidgetList, answerWidgetIdList, correctAnswers, replaceWidget],
  );

  const handleAddChoice = useCallback(() => {
    const currentWidgetData = (store.getState() as RootState).infograph.widgets[widgetId] as MultipleChoicesWidgetData;
    replaceWidget(
      mergeMultipleChoiceWidgetData(
        'add',
        MultipleChoicesType.Horizontal,
        currentWidgetData,
        memberWidgetIds,
        memberWidgetList,
      ),
    );
  }, [widgetId, memberWidgetList, memberWidgetIds, replaceWidget]);

  return (
    <Box w='full'>
      <RadioGroup colorScheme='green' value={correctAnswers[0]} onChange={handleRadioChange}>
        <Flex gap='2' flexDir={'column'}>
          {answerWidgetIdList.map((widgetId, index) => (
            <Flex key={widgetId} justifyContent={'flex-start'} w={'full'} p={0} m={0}>
              <Radio value={index.toString()} w='full'>
                <Text lineHeight={'6'} fontSize={'sm'} color={'font.500'} w={'full'}>
                  {answerWidgetTextList[index]}
                </Text>
              </Radio>
              <Button
                variant='unstyled'
                minW='20px'
                width='20px'
                height='20px'
                onClick={() => handleDeleteChoice(widgetId)}
              >
                <DeleteIcon width='20px' height='20px' color='var(--vg-colors-font-500)' />
              </Button>
            </Flex>
          ))}
        </Flex>
      </RadioGroup>
      <Button variant='action' isDisabled={disableAddButton} borderRadius={1} w='full' mt={4} onClick={handleAddChoice}>
        {t('settingsSideBar.multipleChoices.addButton')}
      </Button>
    </Box>
  );
};

/**
 * Render the question section on the side panel settings
 */
export const QuestionSection = memo(() => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'multipleChoicesToolbar', useSuspense: false });
  const { updateWidget, enableMultipleSelection, correctAnswers } = useWidget<MultipleChoicesWidgetData>();
  const [flag, setFlag] = useBoolean(enableMultipleSelection);

  // Toggle between Radio <-> Checkbox component
  const handleSwitchChange = useCallback(() => {
    // Parse the correctAnswers to swap Radio <-> Checkbox component
    const newCorrectAnswers = flag ? (correctAnswers.length ? [correctAnswers[0]] : ['0']) : correctAnswers;

    // Update side setting toolbar state
    updateWidget({
      enableMultipleSelection: !flag,
      correctAnswers: newCorrectAnswers,
    });

    setFlag.toggle();

    // Mixpanel event
    Mixpanel.track(QUIZZES_EDITED.eventName, {
      edit_type: !flag ? QUIZZES_EDITED.edit_type.on : QUIZZES_EDITED.edit_type.off,
    });
  }, [flag, setFlag, correctAnswers, updateWidget]);

  return (
    <VStack>
      <QuestionText />
      <Flex w={'full'} textAlign={'left'} justifyContent='space-between'>
        <Text fontSize={'sm'} fontWeight='bold' my={2} textAlign={'left'}>
          {t('settingsSideBar.multipleChoices.correct')}
        </Text>
        <Flex flexDir='row' alignItems='center'>
          <Text fontSize='sm'>Multiple Selection</Text>
          <Switch size='md' pl={4} isChecked={flag} onChange={handleSwitchChange} />
        </Flex>
      </Flex>
      {flag ? <CheckBoxCorrectSection /> : <RadioCorrectSection />}
    </VStack>
  );
});

QuestionSection.displayName = 'Question Section';

export default QuestionSection;
